import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sb } from '../supabase';
import { CUSTOMIZATION_CHIPS, labelFor } from '../lib/requestFormOptions';
import {
  supplierTermRows, prefillPriceBasis, deliveryAskText, deliveryShortfallText,
} from '../lib/supplierRequestTerms';
import { displayCategoryForCode } from '../lib/factoryCategories';
import { publicRfqStrings, publicRfqErrorText } from './publicRfqStrings';
import { cityInEnglish } from '../lib/cityNames';
import QuoteFields, { SAMPLE_ASKED, validateQuoteCore } from '../components/quote/QuoteFields';
import DeliveryCapability from '../components/quote/DeliveryCapability';
import { quoteStrings } from '../components/quote/quoteStrings';
import { useAdminCnyRate } from '../lib/useAdminCnyRate';
import {
  loadDeliveryTerms, saveDeliveryTerms, allowedBasesFor, defaultBasisFor,
} from '../lib/supplierDeliveryTerms';
import { captureUtm } from '../lib/utmCapture';
import TranslatedText from '../components/TranslatedText';

// Public RFQ page + quote form. ONE route serves both halves of the flow
// (/rfq/:token/quote): signed out it reads the request and offers registration,
// signed in it collects the quote. Keeping them on one URL is the whole point —
// it is what `?next=` returns to, so registration and quoting happen in one
// session and nobody is dropped on a generic dashboard.
//
// Why this is not /f/:slug: that slug is a key to ONE factory, and pressing
// "respond" begins a claim of THAT factory's identity. Posting it publicly
// hands the factory page to whoever completes the form first. This token is
// scoped to a REQUEST and grants nothing.
//
// Everything on screen comes from get_public_rfq(token), which withholds the
// trader's name, request_id, contact details, budget and attachments. The page
// cannot leak what it was never given — the gate is the RPC, not this file.

// Stage 1 of the two-bar model — the light visibility bar (licence + contact +
// category). NOT /dashboard?tab=verification, which is the eight-field PAYOUT
// file: sending someone there straight after a quote is the exact friction the
// two-bar model exists to remove.
const POST_QUOTE_ROUTE = '/supplier/verify';


const wrap = { minHeight: 'var(--app-dvh)', paddingTop: 'var(--page-top-offset)', background: 'var(--bg-base)' };
const shellBase = { maxWidth: 680, margin: '0 auto', padding: '40px 20px 80px' };
const card = { background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 24, marginBottom: 20 };
const label = { display: 'block', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 };
const input = { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 15, boxSizing: 'border-box' };
const btn = { width: '100%', padding: '14px 18px', borderRadius: 8, border: 'none', background: 'var(--text-primary)', color: 'var(--bg-base)', fontSize: 15, cursor: 'pointer' };
const errText = { color: 'var(--danger, #c0392b)', fontSize: 14, lineHeight: 1.7, margin: '12px 0 0' };

export default function PublicRfqQuote({ lang }) {
  const { token } = useParams();
  const nav = useNavigate();
  // The quote-core labels come from the SHARED pack so the wording matches the
  // dashboard's form exactly; everything else on this page is its own copy.
  const t = { ...publicRfqStrings(lang), ...quoteStrings(lang) };
  const cnyPerUsd = useAdminCnyRate();
  const isAr = lang === 'ar';
  const shell = { ...shellBase, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };

  // Dates are formatted in the READER's language but always on the Gregorian
  // calendar with Latin digits. `ar-SA` alone resolves to the Islamic calendar
  // in ICU, which would render a shipping deadline in a calendar the trader
  // never chose it in — a silent off-by-a-lot on the one field the whole page
  // is racing against.
  const dateLocale = lang === 'zh' ? 'zh-CN' : isAr ? 'ar-EG-u-ca-gregory-nu-latn' : 'en-GB';
  const fmtDate = (v, opts) => new Date(v).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric', ...opts });

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState(null);
  // The registered-supplier view: the specification the quote is priced against.
  const [details, setDetails] = useState(null);
  // path -> short-lived signed URL. Empty until minted, and never populated
  // for a signed-out visitor: get_rfq_details_for_supplier is what returns
  // the paths at all, and it refuses anyone who is not a registered supplier.
  const [attachUrls, setAttachUrls] = useState({});
  const [attachFailed, setAttachFailed] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [done, setDone] = useState(false);

  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [moq, setMoq] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [note, setNote] = useState('');
  const [priceBasis, setPriceBasis] = useState('');
  const [validDays, setValidDays] = useState(30);   // the owner's default
  const [sampleAvailable, setSampleAvailable] = useState(false);
  const [samplePrice, setSamplePrice] = useState('');
  const [sampleDays, setSampleDays] = useState('');
  const [origin, setOrigin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // The supplier's stored capability, and whether we still need to ask.
  // `askCapability` is decided ONCE, on load — not derived from the live
  // selection — so the block does not vanish under the supplier's hands the
  // moment they tick the first box.
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [askCapability, setAskCapability] = useState(false);

  // The sample block appears ONLY when the request asks for one. Every extra
  // field costs completions, and most requests want no sample at all — so this
  // is driven by the trader's own sample_requirement rather than shown to
  // everyone and ignored.
  const askSample = SAMPLE_ASKED.includes(String(details?.sample_requirement || ''));

  // Capture the channel BEFORE anything can navigate away: the query string is
  // gone once they walk to /login to register, and signup is where it is read
  // back (item 8). Untagged visits store nothing and clear nothing.
  useEffect(() => { captureUtm(window.location.search); }, []);

  // The request is re-read whenever the reader changes language, because the
  // title and specification now come back in p_lang. Two things must not come
  // with that re-read:
  //   · the full-page loading state, which would blank content already on
  //     screen every time someone tries the language switch; and
  //   · a second record_rfq_link_route, which is about holding the TOKEN, not
  //     about how many times the page was rendered.
  const loadedOnceRef = useRef(false);
  const routeRecordedRef = useRef(false);

  /**
   * Mint short-lived signed URLs for this request's attachments.
   *
   * Fire-and-forget: an attachment that cannot be signed must never stop the
   * supplier reading the specification or sending a quote. The three failure
   * branches look IDENTICAL on screen -- the rows render disabled -- so they
   * are separated in the console, the same lesson FactoryRequestView records:
   * a 401/403 is an authorisation or data problem, a 5xx is an outage, a throw
   * never reached the function at all.
   */
  const mintAttachmentUrls = useCallback((paths) => {
    if (!Array.isArray(paths) || paths.length === 0) return;
    sb.functions.invoke('rfq-attachment-url', { body: { token, paths } })
      .then(({ data, error }) => {
        if (error) {
          console.error(
            '[PublicRfqQuote] attachment URLs not minted:',
            error.context?.status ?? error.status ?? 'no status',
            error.message || error,
          );
          setAttachFailed(true);
          return;
        }
        if (data?.urls) setAttachUrls(data.urls);
      })
      .catch((e) => {
        console.error('[PublicRfqQuote] attachment URL request threw:', e?.message || e);
        setAttachFailed(true);
      });
  }, [token]);

  const load = useCallback(async () => {
    if (!loadedOnceRef.current) setLoading(true);
    // Session first: an expired one must render the signed-out CTA, not a form
    // that will fail on submit with RFQ_AUTH_REQUIRED.
    let hasSession = false;
    try {
      const { data } = await sb.auth.getSession();
      hasSession = !!data?.session?.user;
    } catch (sessionErr) {
      console.error('[PublicRfqQuote] getSession failed:', sessionErr?.message || sessionErr);
    }
    setSignedIn(hasSession);

    // A signed-in supplier gets the fuller view, which is a superset of the
    // public one — so one call, not two. A signed-in BUYER is refused
    // (RFQ_NOT_A_SUPPLIER) and falls through to the public view rather than
    // seeing an error page for a request they are perfectly entitled to read.
    if (hasSession) {
      const { data: full, error: fullErr } = await sb.rpc('get_rfq_details_for_supplier', { p_token: token, p_lang: lang });
      if (!fullErr) {
        const row = Array.isArray(full) ? full[0] : full;
        setRfq(row);
        setDetails(row);
        // Only the registered-supplier RPC returns paths, so this cannot fire
        // on the public view even by accident.
        setAttachFailed(false);
        mintAttachmentUrls(row?.attachment_paths);
        loadedOnceRef.current = true;
        setLoading(false);
        // Capability: fire-and-forget. A failure costs the prefill and
        // nothing else — it must never stand between a factory and a quote.
        (async () => {
          const { data: sess } = await sb.auth.getUser();
          const uid = sess?.user?.id;
          if (!uid) return;
          const terms = await loadDeliveryTerms(sb, uid);
          setDeliveryTerms(terms);
          setAskCapability(terms.length === 0);
          // What the TRADER asked for first (2026-09-28), else the supplier's
          // single saved capability. Only prefill an UNTOUCHED field: never
          // overwrite a choice the supplier already made on this form.
          const pre = prefillPriceBasis(row?.delivery_preference, defaultBasisFor(terms));
          if (pre) setPriceBasis((cur) => cur || pre);
        })();
        // Holding the token IS a route (item E). RLS cannot see what token a
        // browser holds, so viewing a live link records it — that is what puts
        // this request in the supplier's "Requests for you" list and lets them
        // come back to it without the link. Fire-and-forget: a failure here
        // must never block reading the request, and the RPC returns a silent
        // false for a dead link rather than throwing.
        if (row?.link_status === 'active' && !routeRecordedRef.current) {
          routeRecordedRef.current = true;
          sb.rpc('record_rfq_link_route', { p_token: token })
            .then(({ error: routeErr }) => {
              if (routeErr) console.error('[PublicRfqQuote] record_rfq_link_route failed:', routeErr.code || '', routeErr.message || routeErr);
            }, (e) => console.error('[PublicRfqQuote] record_rfq_link_route threw:', e?.message || e));
        }
        return;
      }
      console.error('[PublicRfqQuote] get_rfq_details_for_supplier failed:', fullErr.code || '', fullErr.message || fullErr);
    }

    const { data, error } = await sb.rpc('get_public_rfq', { p_token: token, p_lang: lang });
    if (error) {
      console.error('[PublicRfqQuote] get_public_rfq failed:', error.code || '', error.message || error);
      setRfq({ link_status: 'not_found' });
    } else {
      setRfq(Array.isArray(data) ? data[0] : data);
      setDetails(null);
    }
    loadedOnceRef.current = true;
    setLoading(false);
  }, [token, lang, mintAttachmentUrls]);

  useEffect(() => { loadedOnceRef.current = false; routeRecordedRef.current = false; }, [token]);
  useEffect(() => { load(); }, [load]);

  const goRegister = () => {
    // ?next= comes back to THIS url. sanitizeNextPath is a prefix check, not an
    // allowlist, so no route registration is needed for it to survive.
    nav(`/login/supplier?mode=signup&next=${encodeURIComponent(`/rfq/${token}/quote`)}`);
  };

  const goSignIn = () => {
    nav(`/login/supplier?next=${encodeURIComponent(`/rfq/${token}/quote`)}`);
  };

  const submit = async () => {
    setErr('');
    // The SAME rule the dashboard runs — "same fields" has to mean same
    // validation, or the two doors diverge again by the back way.
    const bad = validateQuoteCore({ price, leadTime, priceBasis });
    if (bad) { setErr(t[bad]); return; }
    const priceNum = Number(price);
    const days = Number.parseInt(leadTime, 10);

    // Only send the sample trio when a sample is actually offered. The RPC
    // nulls price and days when sample_available is false anyway; not sending
    // them keeps the payload honest about what the supplier chose.
    const sampleFields = askSample
      ? {
        p_sample_available: sampleAvailable,
        p_sample_price: sampleAvailable && samplePrice.trim() !== '' ? Number(samplePrice) : null,
        p_sample_days: sampleAvailable && sampleDays.trim() !== '' ? Number.parseInt(sampleDays, 10) : null,
      }
      : {};

    setBusy(true);

    // Persist the capability BEFORE the quote, so a supplier who answers it
    // here never has to answer it again — even if the quote itself is then
    // refused (a duplicate, a closed request). Best-effort: saveDeliveryTerms
    // logs and returns false rather than throwing, and a failure must not
    // cost them the quote they came to send.
    if (askCapability && deliveryTerms.length > 0) {
      const { data: sess } = await sb.auth.getUser();
      if (sess?.user?.id) await saveDeliveryTerms(sb, sess.user.id, deliveryTerms);
    }

    const { error } = await sb.rpc('submit_public_rfq_quote', {
      p_token: token,
      p_price: priceNum,
      p_currency: currency,
      p_moq: moq.trim() || null,
      p_delivery_days: days,
      p_note: note.trim() || null,
      p_price_basis: priceBasis,
      p_valid_days: Number(validDays),
      p_origin: origin.trim() || null,
      ...sampleFields,
    });
    setBusy(false);

    if (error) {
      // Log the provider's own message (security rule 4) but never render it:
      // the reader is a factory owner mid-signup and a raw string is a refusal
      // with no next step.
      console.error('[PublicRfqQuote] submit_public_rfq_quote failed:', error.code || '', error.message || error);
      setErr(publicRfqErrorText(error, t));
      return;
    }
    setDone(true);
  };

  if (loading) {
    return <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}><p style={{ color: 'var(--text-tertiary)' }}>{t.stateLoading}</p></div></div>;
  }

  const status = rfq?.link_status || 'not_found';

  if (status !== 'active') {
    return (
      <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}>
        <div style={card}>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            {t.states[status] || t.states.not_found}
          </p>
        </div>
        {/* A closed request is still a reason to register — that is the whole
            point of the channel. A dead link is not. */}
        {status === 'closed' && (
          <button style={btn} onClick={goRegister}>{t.ctaButton}</button>
        )}
      </div></div>
    );
  }

  if (done) {
    return (
      <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}>
        <div style={card}>
          <h1 style={{ margin: '0 0 12px', fontSize: 26, fontWeight: 400, color: 'var(--text-primary)' }}>{t.doneTitle}</h1>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--text-secondary)' }}>{t.doneBody}</p>
        </div>
        <button style={btn} onClick={() => nav(POST_QUOTE_ROUTE)}>{t.doneCta}</button>
      </div></div>
    );
  }

  const cat = displayCategoryForCode(rfq.category);
  const catLabel = cat ? (cat.label?.[lang] || cat.label?.en || rfq.category) : rfq.category;
  const buyerBadge = rfq.trader_verified ? t.buyerVerified : t.buyerRegistered;
  // profiles.city is free text and usually Arabic. For a Chinese or English
  // reader an unmapped value is DROPPED rather than rendered — Arabic script
  // tells that reader nothing (blocker, owner, 2026-09-19: "Registered Buyer
  // — جدة"). An ARABIC reader gets it as the trader typed it: there the
  // script is the point, and mapping it to English would be the same defect
  // pointed the other way.
  const buyerLine = [
    isAr ? (rfq.trader_city || '') : cityInEnglish(rfq.trader_city),
    rfq.trader_country,
  ].filter(Boolean).join(', ');
  const quotes = Number(rfq.quote_count) || 0;

  // Storage paths are {uid}/{session}/{uniq}_{safeName} (useRequestAttachments).
  // Show the supplier the filename the trader picked, never the uid segment --
  // that is the trader's user id, and this page exists to keep the two parties
  // from identifying each other outside the platform.
  // The trader's own filename is often UNRECOVERABLE, and the page has to cope
  // rather than print the wreckage. useRequestAttachments runs
  //   safeName = name.replace(/[^\w.-]+/g, '_')
  // so an Arabic or Chinese filename -- which is most of them, on this product
  // -- collapses to underscores. Production's only attachment today is stored
  // as `mu8b2cer1c__.jpg`, and the naive "everything after the first _" gives
  // `_.jpg`, which reads as a broken page rather than as a file.
  //
  // So: use the recovered name only when there is something left of it, and
  // otherwise label the file by position and extension. Numbering is the
  // caller's job because only it knows the index.
  const attachName = (path, index) => {
    const base = String(path || '').split('/').pop() || '';
    const cut = base.indexOf('_');
    const recovered = (cut >= 0 ? base.slice(cut + 1) : base).trim();
    // Strip the extension to judge whether any real name survived. `dot >= 0`
    // rather than `dot > 0`: when safeName eats the entire stem the recovered
    // string is just ".jpeg", where the dot sits at index 0 and a `> 0` test
    // reads it as a dotfile with no extension -- returning ".jpeg" as the
    // supplier-visible filename.
    const dot = recovered.lastIndexOf('.');
    const hasExt = dot >= 0 && dot < recovered.length - 1;
    const stem = hasExt ? recovered.slice(0, dot) : recovered;
    const ext = hasExt ? recovered.slice(dot) : '';
    if (stem.replace(/[_\-.\s]/g, '') !== '') return recovered;
    return `${t.attachments} ${index + 1}${ext}`;
  };
  const attachments = (signedIn && Array.isArray(details?.attachment_paths))
    ? details.attachment_paths.filter(Boolean) : [];

  // The trader's terms come from the shared builder (2026-09-28), so a factory
  // reads the same rows and wording here as on /f/, the dashboard and the app.
  // SIGNED-OUT: `rfq` is a get_public_rfq row — the builder can only find the
  // public facts in it (quantity, destination, delivery ask); the target
  // price is not in that RPC and stays off a public link (owner, 2026-09-28).
  // SIGNED-IN: `details` is get_rfq_details_for_supplier, which adds payment
  // plan, sample, timeframe and — once the proposed SQL is applied — the
  // target price. Missing columns drop their row.
  const term = (src, key) => {
    const r = supplierTermRows(src, lang, { only: [key] })[0];
    return r ? { k: r.label, v: r.value } : null;
  };
  const detailRows = details ? [
    details.description ? { k: t.spec, v: details.description, pre: true } : null,
    term(details, 'targetPrice'),
    term(details, 'paymentPlan'),
    term(details, 'sample'),
    (details.customization_types || []).length
      ? {
        k: t.customization,
        // The chips are enum keys and already trilingual via labelFor.
        v: (details.customization_types || [])
          .map((key) => { const c = CUSTOMIZATION_CHIPS.find((x) => x.key === key); return c ? labelFor(c, lang) : key; })
          .join(isAr || lang === 'zh' ? '، ' : ', '),
        // customization_details is the ONLY free text on this page with no
        // pre-translated column behind it: the title and specification are
        // written to *_ar/_en/_zh at creation, and every other row here is an
        // enum. So it is the one field that earns a live translation call.
        // TranslatedText renders the original first and swaps in the
        // translation when it lands, so a slow or failed call costs nothing.
        node: details.customization_details || null,
      }
      : null,
    term(details, 'timeframe'),
  ].filter(Boolean) : [];

  const rows = [
    { k: t.product, v: rfq.product },
    rfq.category ? { k: t.category, v: catLabel } : null,
    term(rfq, 'quantity'),
    // CHANGED 2026-09-21: conditional, and it has to be.
    // This row used to be unconditional because it could not be empty — the
    // RPC ended in coalesce(..., 'Saudi Arabia'), so every factory was told a
    // constant that no trader had typed. ship_country/ship_city had never been
    // written by anything. 20260921122935 removed the literal, which means the
    // value is genuinely null until the request form (20260921122508 gives it
    // its first writer) supplies one — and an empty «الوجهة» row reads as a
    // broken page, not as a missing fact.
    //
    // City first when we have it: "Riyadh, Saudi Arabia" is what a factory
    // prices freight against; the country alone is what they already assumed.
    term(rfq, 'destination'),
    // What the trader asked for, told to the supplier with the Incoterm to
    // quote. Public on purpose: both RPCs return delivery_preference, and a
    // factory cannot price a link without knowing where the goods must land.
    term(rfq, 'delivery'),
    // No deadline → no row. "No deadline set" reads as an RFQ nobody is
    // waiting on, which is the opposite of the urgency the link is for. Our
    // editorial rule is that nothing ships without a deadline, but the page
    // fails safe rather than trusting that.
    // Rendered in the TRADER's timezone, not the reader's. The admin stores
    // end-of-day Riyadh (20:59:59Z), so a factory in China (UTC+8) formatting
    // in local time would read every deadline as the following day — a
    // guaranteed off-by-one for the audience this page exists for. Pinning the
    // zone means the supplier sees the date the trader actually chose.
    rfq.deadline
      ? { k: t.deadline, v: fmtDate(rfq.deadline, { timeZone: 'Asia/Riyadh' }) }
      : null,
  ].filter(Boolean);

  return (
    <div style={wrap} dir={isAr ? 'rtl' : 'ltr'}><div style={shell}>

      <p style={{ margin: '0 0 6px', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t.eyebrow} · {t.via}
      </p>
      <h1 style={{ margin: '0 0 6px', fontSize: 30, fontWeight: 300, color: 'var(--text-primary)' }}>{rfq.product}</h1>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--text-tertiary)' }}>
        {t.ref} {rfq.rfq_ref}
        {rfq.posted_at ? ` · ${t.postedOn} ${fmtDate(rfq.posted_at)}` : ''}
        {' · '}{quotes > 0 ? t.quotesReceived(quotes) : t.beFirst}
      </p>

      <div style={card}>
        {rows.map((r) => (
          <div key={r.k} style={{ display: 'flex', gap: 16, padding: '9px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ flex: '0 0 130px', fontSize: 13, color: 'var(--text-tertiary)' }}>{r.k}</span>
            <span style={{ flex: 1, fontSize: 15, color: 'var(--text-primary)' }}>{r.v}</span>
          </div>
        ))}
        {/* Identity is city + registration status. No name, ever — this page is
            posted publicly and a name in a link preview cannot be withdrawn. */}
        <div style={{ display: 'flex', gap: 16, padding: '9px 0' }}>
          {/* CHANGED 2026-09-20: was {t.eyebrow}. This is a FIELD LABEL in the
              same card as Product / Category / Quantity, and it was rendering
              the page's own title, so the row read
              «طلب عرض سعر: مشترٍ مسجَّل — جدة». `eyebrow` is unchanged and
              still correct at :400, where it IS the eyebrow. */}
          <span style={{ flex: '0 0 130px', fontSize: 13, color: 'var(--text-tertiary)' }}>{t.postedBy}</span>
          <span style={{ flex: 1, fontSize: 15, color: 'var(--text-primary)' }}>
            {buyerBadge}{buyerLine ? ` — ${buyerLine}` : ''}
          </span>
        </div>
      </div>

      {/* Request details — what registration buys. Above the form on purpose:
          a supplier must read the specification before pricing against it, and
          the previous version left them to quote off a four-line summary. */}
      {detailRows.length > 0 && (
        <div style={card}>
          <h2 style={{ margin: '0 0 14px', fontSize: 19, fontWeight: 400, color: 'var(--text-primary)' }}>{t.detailsTitle}</h2>
          {detailRows.map((r) => (
            <div key={r.k} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 6 }}>{r.k}</div>
              <div style={{
                fontSize: 15, color: 'var(--text-primary)', lineHeight: 1.75,
                // The specification arrives as authored, newlines and all.
                // Collapsing it into a paragraph loses the line-per-attribute
                // shape a factory actually reads it in.
                whiteSpace: r.pre ? 'pre-wrap' : 'normal',
              }}>{r.v}</div>
              {r.node && (
                <TranslatedText
                  text={r.node}
                  lang={lang}
                  style={{ fontSize: 15, color: 'var(--text-primary)', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Attachments. REGISTERED SUPPLIERS ONLY, and structurally so: the
          paths arrive from get_rfq_details_for_supplier, which the public view
          never calls. The public card above must never list these -- it is
          posted to social media, and a drawing is the trader's property.

          THE LIMIT, STATED PLAINLY: nothing here screens an image for contact
          details. mask_contact_details() covers TEXT; a photographed business
          card, a WeChat QR code or an Alibaba storefront in the corner of a
          drawing passes through untouched, and we do not OCR. The control is
          the owner's editorial read of the request AND its attachments before
          a public link is minted for it -- an operator responsibility, not a
          system guarantee. Logged in CLAUDE.md. */}
      {attachments.length > 0 && (
        <div style={card}>
          <h2 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 400, color: 'var(--text-primary)' }}>{t.attachments}</h2>
          <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.7, color: 'var(--text-tertiary)' }}>{t.attachmentsHint}</p>
          {attachments.map((path, i) => {
            const url = attachUrls[path];
            return (
              <div key={path} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ flex: 1, fontSize: 14, color: url ? 'var(--text-primary)' : 'var(--text-disabled)', wordBreak: 'break-all' }}>
                  {attachName(path, i)}
                </span>
                {url ? (
                  // noopener/noreferrer: the signed URL is a credential in a
                  // query string, and Referer would carry it to whatever the
                  // file links on to.
                  <a href={url} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                    {t.attachmentOpen}
                  </a>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>…</span>
                )}
              </div>
            );
          })}
          {attachFailed && <p style={{ ...errText, fontSize: 13 }}>{t.attachmentUnavailable}</p>}
        </div>
      )}

      {!signedIn ? (
        <div style={card}>
          <h2 style={{ margin: '0 0 10px', fontSize: 19, fontWeight: 400, color: 'var(--text-primary)' }}>{t.ctaTitle}</h2>
          <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.8, color: 'var(--text-secondary)' }}>{t.ctaBody}</p>
          {/* Name what is behind the door. "Register" with no stated reward is
              a worse ask than "register to see the spec". */}
          <p style={{ margin: '0 0 18px', fontSize: 13, lineHeight: 1.8, color: 'var(--text-tertiary)' }}>{t.detailsLocked}</p>
          <button style={btn} onClick={goRegister}>{t.ctaButton}</button>
          <button
            onClick={goSignIn}
            style={{ width: '100%', marginTop: 10, padding: '11px', background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 14, cursor: 'pointer' }}
          >
            {t.haveAccount}
          </button>
        </div>
      ) : (
        <div style={card}>
          <h2 style={{ margin: '0 0 18px', fontSize: 19, fontWeight: 400, color: 'var(--text-primary)' }}>{t.formTitle}</h2>

          {askCapability && (
            <DeliveryCapability
              lang={lang}
              value={deliveryTerms}
              onChange={setDeliveryTerms}
              t={{ title: t.capTitle, hint: t.capHint }}
              styles={{ label }}
            />
          )}

          <QuoteFields
            t={t}
            idPrefix="rfq"
            cnyPerUsd={cnyPerUsd}
            allowedBases={allowedBasesFor(deliveryTerms)}
            styles={{ label, input, hint: { margin: '4px 0 0', fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 } }}
            askSample={askSample}
            sampleRequirement={details?.sample_requirement || ''}
            deliveryAsk={deliveryAskText(rfq?.delivery_preference, lang)}
            basisWarning={deliveryShortfallText(priceBasis, rfq?.delivery_preference, lang)}
            isAr={isAr}
            values={{ price, currency, moq, leadTime, origin, note, priceBasis, validDays, sampleAvailable, samplePrice, sampleDays }}
            onChange={(k, v) => ({
              price: setPrice, currency: setCurrency, moq: setMoq, leadTime: setLeadTime,
              origin: setOrigin, note: setNote, priceBasis: setPriceBasis, validDays: setValidDays,
              sampleAvailable: setSampleAvailable, samplePrice: setSamplePrice, sampleDays: setSampleDays,
            }[k](v))}
          />

          <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? t.submitting : t.submit}
          </button>
          {err && <p style={errText}>{err}</p>}
        </div>
      )}

    </div></div>
  );
}
